import * as XLSX from "xlsx";

// The candidate list, as a workbook.
//
// ─── ONE FILE, A SHEET PER ROUND ────────────────────────────────────────────
//
// "Stage-wise and all" is what a workbook is FOR. A single flat sheet with a
// Round column means the reader filters it themselves every time; separate
// files per round means five downloads and no way to see the whole pipeline.
// So: a Summary, everyone on one sheet, then one sheet per round in pipeline
// order, then Rejected.
//
// Rejected is last and separate because it answers a different question, and
// it carries two columns nothing else needs — when we said no, and why.
//
// ─── WHAT IT EXPORTS, AND THE HONEST BIT ────────────────────────────────────
//
// The rows the pipeline currently holds. When a date filter is in force those
// rows are already narrowed by the server, so the workbook is narrowed too —
// which would be invisible in a file opened next week. The Summary sheet states
// the filter, and the filename carries the date range. A silently partial
// export of a hiring pipeline is the kind of thing somebody makes a decision
// on.
//
// ─── SHEET NAMES ARE NOT FREE TEXT ──────────────────────────────────────────
//
// Excel refuses a sheet name that is over 31 characters, contains : \ / ? * [ ]
// or is empty, and refuses a workbook with two sheets of the same name. Round
// names come from a text box in the job wizard, so all four are reachable —
// "F2F Technical / Panel" alone breaks two of them. See safeSheetName.

// Columns for the candidate sheets. Order is the order they appear.
//
// Labels, not field names: this file is opened by people who did not write the
// schema. `notice_period_days` becomes "Notice (days)".
const COLUMNS = [
  ["Name", (r) => r.candidate_name || ""],
  ["Email", (r) => r.email || ""],
  ["Phone", (r) => r.phone || ""],
  ["City", (r) => r.city || ""],
  ["Current company", (r) => r.current_company || ""],
  ["Experience (yrs)", (r) => num(r.experience ?? r.years_experience)],
  ["Notice (days)", (r) => num(r.notice_period_days)],
  ["Expected CTC (per year)", (r) => num(r.expected_salary_annual)],
  ["Round", (r) => r.current_stage_name || "—"],
  ["Status", (r) => pretty(r.status)],
  ["Resume score /10", (r) => num(r.fit_score)],
  ["Resume band", (r) => pretty(r.band)],
  ["Interview score", (r) => num(r.interview_score)],
  // REJECTED here is the AI's suggestion from a failed gate, not a decision —
  // worded as on screen, with the reason beside it.
  [
    "Interview verdict",
    (r) => (r.interview_verdict === "REJECTED" ? "Suggest reject" : pretty(r.interview_verdict)),
  ],
  ["Verdict reason", (r) => r.interview_verdict_reason || "—"],
  ["Interview status", (r) => pretty(r.interview_status)],
  ["Applied on", (r) => date(r.applied_at)],
  ["Resume screened on", (r) => date(r.screened_at)],
  ["Interview link sent on", (r) => date(r.invited_at)],
  // Left blank rather than omitted on the live sheets, so every candidate sheet
  // has identical columns and two of them can be pasted together.
  ["Rejected on", (r) => date(r.rejected_at)],
  ["Rejection reason", (r) => r.rejection_reason || ""],
  ["Resume link", (r) => r.resume_url || ""],
];

// Numbers stay numbers so Excel can sum and sort them. `null` becomes an empty
// cell, NOT 0 — "no notice period recorded" and "can start immediately" are
// different facts and 0 would merge them.
function num(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}

// STRONG → Strong. The enums are shouted because they are database values.
function pretty(v) {
  if (!v) return "";
  const s = String(v).replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// `dd-mm-yyyy hh:mm`, as text.
//
// Deliberately not a Date object. SheetJS can write real dates, but then the
// cell renders in whatever the opener's locale decides — and this workbook gets
// mailed around, so 09-02 would read as 9 February to half the recipients and 2
// September to the other half. A fixed unambiguous string is worth losing the
// sortability of a date cell.
function date(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Excel's rules, applied so a round name typed in the job wizard cannot produce
// a corrupt file.
//
//   * : \ / ? * [ ] are illegal in a sheet name
//   * 31 characters maximum
//   * cannot be blank
//   * cannot repeat within one workbook
//
// `used` carries the names already taken, and a clash gets a numeric suffix
// that fits inside the 31-character budget rather than being appended past it.
export function safeSheetName(name, used = new Set()) {
  let base = String(name || "")
    .replace(/[:\\/?*[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 31);
  if (!base) base = "Sheet";

  if (!used.has(base.toLowerCase())) {
    used.add(base.toLowerCase());
    return base;
  }
  for (let i = 2; i < 100; i += 1) {
    const suffix = ` (${i})`;
    const candidate = base.slice(0, 31 - suffix.length) + suffix;
    if (!used.has(candidate.toLowerCase())) {
      used.add(candidate.toLowerCase());
      return candidate;
    }
  }
  // 99 rounds sharing one name. Not reachable in practice, and returning
  // something is better than throwing away the export.
  const fallback = base.slice(0, 24) + " " + Date.now().toString().slice(-6);
  used.add(fallback.toLowerCase());
  return fallback;
}

function sheetFrom(rows) {
  const data = rows.map((r) =>
    COLUMNS.reduce((acc, [label, get]) => {
      acc[label] = get(r);
      return acc;
    }, {}),
  );

  // A header-only sheet for an empty round, rather than a blank one. An empty
  // sheet with no headers reads as "the export broke"; headers with no rows
  // read as "nobody is in this round", which is the fact.
  const sheet = data.length
    ? XLSX.utils.json_to_sheet(data)
    : XLSX.utils.json_to_sheet([], { header: COLUMNS.map(([l]) => l) });

  sheet["!cols"] = COLUMNS.map(([label]) => ({
    wch: Math.max(label.length + 3, 12),
  }));
  // Freezes the header row, so scrolling 300 candidates does not lose which
  // column is which.
  sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
  return sheet;
}

/**
 * Builds and downloads the workbook.
 *
 * @param {object}   args
 * @param {object}   args.job       the job (title, status)
 * @param {Array}    args.rows      candidate rows as the pipeline holds them
 * @param {Array}    args.stages    rounds, in pipeline order: { id, name }
 * @param {object}   [args.filter]  { dateField, from, to } when one is applied
 * @returns {{ filename: string, sheets: number, rows: number }}
 */
export function exportCandidatesWorkbook({ job, rows = [], stages = [], filter }) {
  const wb = XLSX.utils.book_new();
  const used = new Set();

  const live = rows.filter((r) => r.status !== "REJECTED");
  const rejected = rows.filter((r) => r.status === "REJECTED");

  const known = new Set(stages.map((s) => s.id));
  const unplaced = live.filter((r) => !known.has(r.current_stage_id));

  // ── Summary ──────────────────────────────────────────────────────────────
  //
  // First sheet because it is the one that says what this file IS. A workbook
  // of names with no job title and no date is useless a month later, and this
  // is also where the active filter is admitted.
  const summary = [
    { Field: "Job", Value: job?.title || "" },
    { Field: "Job status", Value: pretty(job?.status) },
    { Field: "Exported on", Value: date(new Date()) },
    { Field: "Candidates in this file", Value: rows.length },
    { Field: "In the pipeline", Value: live.length },
    { Field: "Rejected", Value: rejected.length },
    { Field: "", Value: "" },
    {
      Field: "Date filter applied",
      // Stated even when there is none, so its absence is a fact the reader
      // was told rather than something they have to infer.
      Value: filter?.from || filter?.to
        ? `${filter.dateField || "applied"}: ${filter.from || "any"} to ${filter.to || "any"}` +
          " — candidates outside this range are NOT in this file"
        : "None — every candidate on this job is included",
    },
    { Field: "", Value: "" },
    { Field: "ROUND", Value: "CANDIDATES" },
    ...stages.map((s) => ({
      Field: s.name,
      Value: live.filter((r) => r.current_stage_id === s.id).length,
    })),
  ];
  if (unplaced.length) {
    summary.push({ Field: "Not in any round", Value: unplaced.length });
  }

  const summarySheet = XLSX.utils.json_to_sheet(summary);
  summarySheet["!cols"] = [{ wch: 28 }, { wch: 72 }];
  XLSX.utils.book_append_sheet(wb, summarySheet, safeSheetName("Summary", used));

  // ── Everyone ─────────────────────────────────────────────────────────────
  XLSX.utils.book_append_sheet(
    wb,
    sheetFrom(live),
    safeSheetName("All candidates", used),
  );

  // ── One per round, in pipeline order ─────────────────────────────────────
  for (const s of stages) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetFrom(live.filter((r) => r.current_stage_id === s.id)),
      safeSheetName(s.name, used),
    );
  }

  // Only when somebody is actually stranded — the same rule the pipeline's own
  // safety tab follows, so the workbook and the screen agree.
  if (unplaced.length) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetFrom(unplaced),
      safeSheetName("Not in any round", used),
    );
  }

  // ── Rejected, last ───────────────────────────────────────────────────────
  XLSX.utils.book_append_sheet(
    wb,
    sheetFrom(rejected),
    safeSheetName("Rejected", used),
  );

  const stem =
    `${String(job?.title || "candidates").replace(/[^\w-]+/g, "_").slice(0, 60)}` +
    `_${new Date().toISOString().slice(0, 10)}` +
    (filter?.from || filter?.to ? "_filtered" : "");
  const filename = `${stem}.xlsx`;

  XLSX.writeFile(wb, filename);

  return { filename, sheets: wb.SheetNames.length, rows: rows.length };
}

export default exportCandidatesWorkbook;
